import { Flex } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { RanaLoading, RanaModal } from "@nens/ranalib";
import { RanaForm, SubmitFunction } from "@nens/ranalib";
import { capitalizeFirst } from "@nens/shared-utils";

import { cancelButtonText } from "@/translations";
import { ProjectFileOrDirectory } from "@/types/api-model-simple-names";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

import { useRenameProjectFileOrDirectory } from "../api/rename";
import { validateFileName } from "../utils/validate-file-name";

interface ProjectUserRenameModalProps {
  existingFileIds: string[];
  directoryOrFile: ProjectFileOrDirectory;
  onClose: () => void;
}

type FileRenameFormValues = {
  name: string;
};

export function ProjectFileOrDirectoryRenameModal({
  existingFileIds,
  directoryOrFile,
  onClose,
}: ProjectUserRenameModalProps) {
  const intl = useIntl();
  const title = intl.formatMessage({ id: "projectfile.rename", defaultMessage: "Rename" });
  const { isMutating, mutate } = useRenameProjectFileOrDirectory();

  const { id, type } = directoryOrFile;

  const name = getNameFromFileId(id);

  const INITIAL_VALUES: FileRenameFormValues = { name };

  const validate = (values: FileRenameFormValues) =>
    validateFileName(intl, type, existingFileIds, values);

  // Submit can assume validate returned no errors.
  const submit: SubmitFunction<FileRenameFormValues> = async (values, onError) => {
    if (values.name && typeof values.name == "string")
      mutate(
        {
          id,
          type,
          renameTo: values.name,
        },
        {
          onSuccess: () => {
            onClose();
          },
          onError,
        }
      );
  };

  return (
    <RanaForm initialValues={INITIAL_VALUES} validate={validate} doSubmit={submit}>
      <RanaModal
        isOpen
        onClose={onClose}
        topBar={title}
        maxW="xl"
        buttons={[
          {
            children: cancelButtonText,
            variant: "tertiary",
            isDisabled: isMutating,
            onClick: onClose,
          },
          <RanaForm.SubmitButton key={title} isDisabled={isMutating}>
            {title}
          </RanaForm.SubmitButton>,
        ]}
      >
        <Flex flexDir="column" gap={2}>
          <RanaForm.Input
            submitOnEnter
            autoFocus
            field="name"
            label={capitalizeFirst(type) + " name"}
            isRequired
          />
          {isMutating && <RanaLoading size="md" isCentered />}
          <RanaForm.Errors />
        </Flex>
      </RanaModal>
    </RanaForm>
  );
}
